import { useContext } from 'react';
import { ConfigProps } from '../../../../../../components/financialTables/configuration/constants';
import { MetadataField, NEW_REPORT_ID, ReportField, SrcFieldValue } from '../../../../../../constnats/reportConstants';
import Alert from '../../../../../../utils/alert';
import { getReportUniqueBusinessKey } from '../../../../../../utils/common';
import Utils from '../../../../../../utils/utils';
import { RECONCILIATION_OPERATION } from '../../../constants/peerDiff';
import { useGetPeerReportsDiffDetector, usePeerBranchesActions } from '../../../contexts/peersReportsContext';
import { EditRow, ViewRow } from './columnComponents';
import { default as DescriptorUtils } from '../../../../../../utils/descriptor/descriptorUtils';
import TableUpdatesRepo from '../../../../../../utils/repository/tableUpdatesRepo';
import { TableUpdateContext } from '../../../../../../contexts/reviewTypeContext';
import { BranchSettingContext } from '../../../../../../contexts/branchSettingsContext';
import Assert from '../../../../../../utils/asserts';
import { CoreValueDiff } from '../../../utils/peerDiffUtil';

const FONT_WEIGHT_BOLD_STYLE = { fontWeight: "bold" }

function getTableRowStyle(descriptor, row, highlightedRows) {
    const style = JSON.parse(JSON.stringify(descriptor.style))
    if (style.backgroundColor !== undefined) {
        Object.assign(style, FONT_WEIGHT_BOLD_STYLE)
    }

    if (descriptor.note === true) {
        style.color = "#9f9d9d"
    }

    if (highlightedRows.includes(row)) {
        style.backgroundColor = "#ffff99"
    }

    return style
}

function getColumnTableRowsClasses (advancedTableCfg) {
    if(advancedTableCfg[ConfigProps.HORIZONTALS]) {
        return "horizontals";
    }

    return "";
}

export function getIsReportReviewed(report) {
    if (report[ReportField.ID] === NEW_REPORT_ID) {
        return false;
    }

    let fullChecksCount = report[ReportField.FULL_CHECKS_COUNT];
    let visualChecksCount = report[ReportField.VISUAL_CHECKS_COUNT];
    const isFullChecksCountNumber = Utils.isNumber(fullChecksCount);
    const isVisualChecksCountNumber = Utils.isNumber(visualChecksCount)
    let isReportReviewed = false;

    if(!isFullChecksCountNumber && !isVisualChecksCountNumber ) {
        isReportReviewed = true;
    }else {
        if(isFullChecksCountNumber && fullChecksCount > 0) {
            isReportReviewed = true;
        }

        if(isVisualChecksCountNumber  && visualChecksCount> 0) {
            isReportReviewed = true
        }
    }

    return isReportReviewed
}

function ColumnTable({ entries, editMode, highlightedRows, report, advancedTableCfg, reportSource, filterCfg, saveEntries,
     readOnly }) {
    const tableUpdateContext = useContext(TableUpdateContext);
    const branchSettingsContext = useContext(BranchSettingContext);
    const rowsClasses = getColumnTableRowsClasses(advancedTableCfg);
    const isReportReviewed = getIsReportReviewed(report);
    const peerReportsDiffDetector = useGetPeerReportsDiffDetector(getReportUniqueBusinessKey(report),report[ReportField.ID],tableUpdateContext.schemaName)
    const {addReconciliationEvents} = usePeerBranchesActions()

    const peerDiffsDisabled = readOnly || report[ReportField.SRC] === SrcFieldValue.CALCULATED

    const createEvent = (descriptorId, propDiff, operation) => {
        const PROP_MAP = {
            coreValue: 'CORE_VALUE',
            reportedAsLabel: 'REPORTED_AS_LABEL',
            src: 'SRC',
        };

        return {
            schemaId: branchSettingsContext.branchSchemaId,
            reportId: report.id,
            descriptorId: descriptorId,
            property: PROP_MAP[propDiff.prop],
            currentValue: propDiff.currentValue,
            targetValue: propDiff.peerValue,
            operation: operation,
            riskLevel: propDiff.riskLevel
        };
    };

    const handleRejectOperation = (entity, propDiffs) => {
        const events = propDiffs.map((propDiff) => createEvent(entity.descriptorId(), propDiff, RECONCILIATION_OPERATION.REJECT));
        saveEvents(events);
    };

    const handleAcceptOperation = (entity, propDiffs) => {

        Assert.trueVal(entries.every(e => e.getData() === e.getOriginalData()), "handleAcceptOperation") 

        const events = [];

        for (const propDiff of propDiffs) {
            const metadata = entity.getMetadata();
            if (propDiff.prop === 'coreValue') {
                if (metadata[MetadataField.CALCS_DISABLED] !== true &&
                    DescriptorUtils.hasCalculation(entity.descriptor)) {
                    Alert.warn('The value of the field cannot be updated automatically because it is calculated.');
                } else {
                    entity.setData(propDiff.peerValue);


                    events.push(...entries
                        .filter(e => e.getData() !== e.getOriginalData())
                        .map(e => createEvent(e.descriptorId(), new CoreValueDiff({ currentValue: e.getOriginalData(), peerValue: e.getData(),riskLevel: propDiff.riskLevel }), RECONCILIATION_OPERATION.ACCEPT))
                    );
                }
            } else if (propDiff.prop === MetadataField.REPORTED_AS_LABEL) {
                entity.setMetadata({
                    ...metadata,
                    [MetadataField.REPORTED_AS_LABEL]: propDiff.peerValue,
                });
                events.push(createEvent(entity.descriptorId(), propDiff, RECONCILIATION_OPERATION.ACCEPT));
            } else if (propDiff.prop === MetadataField.SRC) {
                if (reportSource === SrcFieldValue.MIXED) {
                    entity.setMetadata({
                        ...metadata,
                        [MetadataField.SRC]: propDiff.peerValue,
                    });
                    events.push(createEvent(entity.descriptorId(), propDiff, RECONCILIATION_OPERATION.ACCEPT));
                } else {
                    Alert.warn(
                        'The field source cannot be configured automatically because the report source is different from MIXED'
                    );
                }
            } else {
                Alert.error(`Unsupported property: ${propDiff.prop}`);
            }
        }

        if (events.length > 0) {
            saveEntries(() => saveEvents(events));
        }
    };

    const saveEvents = (events) => {
        const updateId = tableUpdateContext.updateId;
            TableUpdatesRepo.addReconciliationEvents(
                updateId,
                { updateId, events },
                () => addReconciliationEvents(events),
                (err) => console.log(err)
            );
    };

    //Filter rejected diffs
    const resolveDiffs = (entity, propDiffs, operation) => {
        if (operation === RECONCILIATION_OPERATION.ACCEPT) {
            handleAcceptOperation(entity, propDiffs);
        } else if (operation === RECONCILIATION_OPERATION.REJECT) {
            handleRejectOperation(entity, propDiffs);
        }
    }

    const getTableRow = (entity, verticalLevel) => {
        const style = getTableRowStyle(entity.getDescriptor(), verticalLevel + 1, highlightedRows);
        const peerDiffs = peerDiffsDisabled ? [] : peerReportsDiffDetector({
            descriptorId: entity.descriptorId(), coreValue: entity.getData(),
            fieldSpecificMetadata: entity.getMetadata(), reportSrc: report[ReportField.SRC]
        }).filter(d => d.applyable);

        return editMode === true ? (
            <EditRow key={verticalLevel} {...{ entity, style }}
                showSource={reportSource === SrcFieldValue.MIXED}
                isReportReviewed={isReportReviewed} />
        ) : (
                <ViewRow
                    {...{ entity, style }}
                    key={verticalLevel}
                    yoYGrowthEnabled={filterCfg[ConfigProps.SHOW_YEARLY_GROWTH_PERCENTAGES]}
                    poPGrowthEnabled={filterCfg[ConfigProps.SHOW_PERIOD_GROWTH_PERCENTAGES]}
                    reportPeriod={report[ReportField.PERIOD]}
                    reportId={report[ReportField.ID]}
                    showHighGrowthPercentagesWarning={advancedTableCfg[ConfigProps.SHOW_HIGH_GROWTH_PERCENTAGES_WARNING]}
                    className={rowsClasses}
                    isReportReviewed={isReportReviewed}
                    peerDiffs={peerDiffs}
                    resolveDiff={(propDiffs, operation) => { resolveDiffs(entity, propDiffs, operation) }} />
        );
    };

    return (
        <table className="column-table">
            <tbody>{entries.map(getTableRow)}</tbody>
        </table>
    );
}

export default ColumnTable;
